import { useEffect, useState } from 'react'
import liff from '@line/liff'
import { initLiffOnce } from '../../../context/LiffProfileContext'

const MUTELU_LOGIN_TARGET_KEY = 'mutelu_login_target_path'
const MUTELU_LIFF_ID = process.env.REACT_APP_MUTELU_LIFF_ID || process.env.REACT_APP_LIFF_ID
const saveLineProfileToStorage = (profile) => {
  const accessToken = liff.getAccessToken()
  if (accessToken) localStorage.setItem('authToken', accessToken)
  if (profile?.userId) localStorage.setItem('userId', profile.userId)
  if (profile?.displayName) localStorage.setItem('line_display_name', profile.displayName)
  if (profile?.pictureUrl) localStorage.setItem('line_picture_url', profile.pictureUrl)
}

const clearLineProfileFromStorage = () => {
  localStorage.removeItem('authToken')
  localStorage.removeItem('userId')
  localStorage.removeItem('line_display_name')
  localStorage.removeItem('line_picture_url')
}

const LIFF_QUERY_KEYS = new Set(['liff.state', 'liff.clientId', 'liff.redirectUri', 'code', 'state'])

const buildSafeRedirectUri = () => {
  const url = new URL(window.location.href)
  const keys = Array.from(url.searchParams.keys())
  keys.forEach((key) => {
    if (LIFF_QUERY_KEYS.has(key)) {
      url.searchParams.delete(key)
    }
  })
  return `${url.origin}${url.pathname}${url.search}${url.hash}`
}

const getMuteluPathFromLiffState = () => {
  const params = new URLSearchParams(window.location.search)
  const raw = params.get('liff.state')
  if (!raw) return null

  let decoded = raw
  try {
    decoded = decodeURIComponent(raw)
  } catch (error) {}

  if (decoded.startsWith('/')) {
    return decoded
  }

  try {
    const parsed = new URL(decoded)
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch (error) {}

  return null
}

const cleanupLiffQueryParamsFromUrl = () => {
  const url = new URL(window.location.href)
  const keys = Array.from(url.searchParams.keys())
  keys.forEach((key) => {
    if (LIFF_QUERY_KEYS.has(key)) {
      url.searchParams.delete(key)
    }
  })
  const nextPath = `${url.pathname}${url.search}${url.hash}`
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextPath !== currentPath) {
    window.history.replaceState({}, document.title, nextPath)
  }
}

const getDecodedLineToken = () => {
  try {
    if (typeof liff.getDecodedIDToken === 'function') {
      return liff.getDecodedIDToken() || null
    }
  } catch (error) {}
  return null
}

const saveResolvedIdentity = ({ userId, displayName, pictureUrl }) => {
  const accessToken = liff.getAccessToken()
  if (accessToken) localStorage.setItem('authToken', accessToken)
  if (userId) localStorage.setItem('userId', userId)
  if (displayName) localStorage.setItem('line_display_name', displayName)
  if (pictureUrl) localStorage.setItem('line_picture_url', pictureUrl)
}

export default function useMuteluLineProfile() {
  const [ready, setReady] = useState(false)
  const [lineLoggedIn, setLineLoggedIn] = useState(false)

  const handleLogin = () => {
    const liffId = MUTELU_LIFF_ID
    if (!liffId) return
    try {
      const safeRedirectUri = buildSafeRedirectUri()
      const safeRedirectPath = `${new URL(safeRedirectUri).pathname}${new URL(safeRedirectUri).search}${new URL(safeRedirectUri).hash}`
      sessionStorage.setItem(MUTELU_LOGIN_TARGET_KEY, safeRedirectPath)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      const inClient = typeof liff.isInClient === 'function' ? liff.isInClient() : false

      if (isMobile && !inClient) {
        const lineAppUrl = `https://line.me/R/app/${liffId}?liff.state=${encodeURIComponent(safeRedirectPath)}`
        console.log('[mutelu] redirect to LINE app login', {
          liffId,
          lineAppUrl,
          redirectPath: safeRedirectPath
        })
        window.location.href = lineAppUrl
        return
      }

      console.log('[mutelu] trigger LIFF login', {
        liffId,
        redirectUri: safeRedirectUri,
        inClient
      })
      liff.login({ redirectUri: safeRedirectUri })
    } catch (error) {
      console.warn('[mutelu] LINE login trigger failed:', error)
    }
  }

  useEffect(() => {
    let mounted = true

    const syncLineProfile = async () => {
      try {
        if (!MUTELU_LIFF_ID) {
          if (!mounted) return
          const storedLineId = localStorage.getItem('userId')
          setLineLoggedIn(Boolean(storedLineId))
          setReady(true)
          return
        }

        await initLiffOnce(MUTELU_LIFF_ID)

        const liffStatePath = getMuteluPathFromLiffState()
        const pendingLoginTarget = sessionStorage.getItem(MUTELU_LOGIN_TARGET_KEY) || ''

        console.log('[mutelu] line auth checkpoint', {
          liffId: MUTELU_LIFF_ID,
          path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          hasLiffState: Boolean(liffStatePath),
          pendingLoginTarget: pendingLoginTarget || null,
          liffLoggedIn: typeof liff.isLoggedIn === 'function' ? liff.isLoggedIn() : false
        })

        if (liffStatePath && liffStatePath.startsWith('/game/mutelu')) {
          const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
          if (currentPath !== liffStatePath) {
            sessionStorage.setItem(MUTELU_LOGIN_TARGET_KEY, liffStatePath)
            window.location.replace(liffStatePath)
            return
          }
        }
        cleanupLiffQueryParamsFromUrl()

        if (!liff.isLoggedIn()) {
          const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
          if (currentPath.startsWith('/game/mutelu')) {
            sessionStorage.setItem(MUTELU_LOGIN_TARGET_KEY, currentPath)
          }

          clearLineProfileFromStorage()
          if (!mounted) return
          setLineLoggedIn(false)
          setReady(true)

          handleLogin()
          return
        }

        let profile = null
        try {
          profile = await liff.getProfile()
          saveLineProfileToStorage(profile)
        } catch (profileError) {
          console.warn('[mutelu] getProfile failed, fallback to decoded token/local storage:', profileError)
        }

        const decoded = getDecodedLineToken()
        const resolvedUserId =
          profile?.userId ||
          decoded?.sub ||
          localStorage.getItem('userId') ||
          ''
        const resolvedDisplayName =
          profile?.displayName ||
          decoded?.name ||
          localStorage.getItem('line_display_name') ||
          ''
        const resolvedPictureUrl =
          profile?.pictureUrl ||
          decoded?.picture ||
          localStorage.getItem('line_picture_url') ||
          ''

        saveResolvedIdentity({
          userId: resolvedUserId,
          displayName: resolvedDisplayName,
          pictureUrl: resolvedPictureUrl
        })

        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
        if (
          resolvedUserId &&
          pendingLoginTarget &&
          pendingLoginTarget.startsWith('/game/mutelu') &&
          currentPath !== pendingLoginTarget
        ) {
          sessionStorage.removeItem(MUTELU_LOGIN_TARGET_KEY)
          window.location.replace(pendingLoginTarget)
          return
        }

        if (resolvedUserId) {
          sessionStorage.removeItem(MUTELU_LOGIN_TARGET_KEY)
        }

        if (!mounted) return
        setLineLoggedIn(Boolean(resolvedUserId))
      } catch (error) {
        console.warn('[mutelu] LINE profile sync failed:', error)
        if (mounted) {
          const storedLineId = localStorage.getItem('userId')
          if (storedLineId) {
            setLineLoggedIn(true)
          } else {
            clearLineProfileFromStorage()
            setLineLoggedIn(false)
          }
        }
      } finally {
        if (!mounted) return
        setReady(true)
      }
    }

    syncLineProfile()
    return () => {
      mounted = false
    }
  }, [])

  return { ready, lineLoggedIn, handleLogin }
}
